import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Params, Router} from '@angular/router';
import * as dayjs from 'dayjs';
import {Observable, Subject, Subscription} from 'rxjs';
import {LoadingItem} from '../../../shared/interfaces/loading-item';
import {LoadingService} from '../../../shared/services/loading.service';
import {LocationService} from '../../../shared/services/location.service';
import {ProjectService} from '../../../shared/services/project.service';
import {SEOService} from '../../../shared/services/seo.service';
import {IHolidayDetail} from './interfaces/iholiday-detail';
import {IHolidayDetailParams} from './interfaces/iholiday-detail-params';
import {HolidayDetailService} from './services/holiday-detail.service';
import {BookingService} from 'src/app/modules/shared/services/booking.service';
import {FormatAgesPipe} from 'src/app/modules/shared/pipes/format-ages.pipe';
import {GetDataFromMappingPipe} from 'src/app/modules/shared/pipes/get-data-from-mapping.pipe';
import {HolidayBookingService} from '../holiday-booking-page/services/holiday-booking.service';
import Swal from 'sweetalert2';
import {IHolidayDetailSolution} from './interfaces/iholiday-detail-solution';
import {CmsService} from 'src/app/modules/cms/services/cms.service';
import {NavigationService} from '../../../../services/navigation.service';
import {PostMessageService} from '../../../shared/services/post-message.service';
import {WidgetService} from '../../../shared/services/widget.service';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';
import { Occupancy } from 'src/app/modules/shared/interfaces/occupancy';
import { IPrefetchDateParams } from 'src/app/modules/shared/interfaces/iprefetch-date-params';
import { HolidayPrefetchService } from 'src/app/modules/shared/services/holiday-prefetch.service';

@Component({
  selector: 'app-holiday-detail',
  templateUrl: './holiday-detail.component.html',
  styleUrls: ['./holiday-detail.component.scss']
})
export class HolidayDetailComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject();

  public selectedOccupancy: Occupancy;
  public selectedDepartureDate: Date;
  public newApiCallDebouncer$: Subject<boolean> = new Subject<boolean>();
  public loadingItem: LoadingItem = {id: 'holiday-detail', message: 'Stiamo preparando l\'offerta per te...'};

  onlyNetQuotes: boolean | null = null;
  selectedText: number;
  bookingUrl: string;
  detailUrl: string;
  noDetail: boolean;

  holidayDetails: IHolidayDetail;
  holidayDetailsSubscription: Subscription;

  selectedSolution: IHolidayDetailSolution;
  selectedSolutionSubscription: Subscription;

  guestsAges: number[];
  guestsAgesOld: number[]; // nel caso di cambiamenti a numero ed età di guests, tiene traccia degli ultimi parametri che hanno restituito soluzioni valide

  formattedGuestsString: string;
  ocupancySubscription: Subscription;

  routeParams: any;
  destinationName: string;

  fixedRecap: boolean = false;
  isLanding: boolean;
  brand: any;

  isWidget$: Observable<boolean>;

  allSolutionsToShow: IHolidayDetailSolution[];
  allAvailableDates: Date[];
  showNoAvailabilityDetail = false;

  constructor(private _holidayDetailService: HolidayDetailService,
              private _holidayBookingService: HolidayBookingService,
              private _bookingService: BookingService,
              private _seoService: SEOService,
              private _loadingService: LoadingService,
              private _projectService: ProjectService,
              private _locationService: LocationService,
              private _route: ActivatedRoute,
              private _formatAges: FormatAgesPipe,
              private _getDataFromMapping: GetDataFromMappingPipe,
              private _cmsService: CmsService,
              private _navigationService: NavigationService,
              private _postMessageService: PostMessageService,
              private _holidayPrefetchService: HolidayPrefetchService,
              private _widgetService: WidgetService,
              private _router: Router
  ) {
  }

  //  Gestione del recap fisso sul bottom della view da mobile
  @HostListener('window:scroll', ['$event'])
  onWindowScroll($event) {
    this.fixedRecap = window.scrollY >= 100 ? true : false;
    //  Per evitare che il recap fisso sul bottom copra parte del footer aggiungo a quest'ultimo un margin-bottom della stessa altezza del recap
    const recapContainer = (document.querySelector('div.detail-recap__container') as HTMLElement);
    if (!recapContainer || this.isLanding) {
      return;
    }
    const offset = recapContainer?.offsetHeight;
    if (window.innerWidth <= 992 && this.fixedRecap && document.querySelector('footer')) {
      document.querySelector('footer').style.marginBottom = offset + 'px';
    } else if (document.querySelector('footer')) {
      document.querySelector('footer').style.marginBottom = '0';
    }
  }

  ngOnInit(): void {
    this.isWidget$ = this._widgetService.isWidget();

    //  Salva url di dettaglio (href corrente)
    this._locationService.getCurrentHref().toPromise().then(url => this.detailUrl = url);

    this._route.paramMap.subscribe(e => {
      //  Salva paramtri di rotta in variabile globale
      // PORKAROUND landing pages

      const segments = this._route.snapshot['_urlSegment'].segments.map(x => x.path);
      this.isLanding = segments[0] == 'landing';

      this.routeParams = {
        dateFrom: this.isLanding ? segments[10] : e.get('dateFrom'),
        dateTo: this.isLanding ? segments[12] : e.get('dateTo'),
        packetId: this.isLanding ? segments[5] : e.get('packetId'),
        productId: this.isLanding ? segments[3] : e.get('productId'),
        topId: this.isLanding ? segments[4] : e.get('topId'),
        pluginSource: this.isLanding ? segments[6] : e.get('pluginSource'),
        title: this.isLanding ? segments[8] : e.get('holidayTitle'),
        ages: e.get('ages')?.split(',').map(age => Number(age)) || null,
        duration: e.get('duration') || 0
      };

      //  Se l'età dei passeggeri è presente nei parametri di rotta la prende da lì' e la passa al service
      if (this.routeParams.ages) {
        this._bookingService.setNewAges(this.routeParams.ages);
        //  altrimenti la richiede al service (che restituirà un'età impostata precedentemente o il 35,35 di default)
      } else {
        this.routeParams.ages = this._bookingService.getAgesArray();
      }

      this._projectService.hasOnlyNetQuotes().toPromise()
        .then(o => this.onlyNetQuotes = o);

      this._cmsService.brandConfig$.subscribe(brand => {
        if (brand) {
          this.brand = brand;
        }

        //  Occupancy Subscription
        this.ocupancySubscription = this._bookingService.occupancy$.subscribe(occupancy => {
          this.guestsAges = occupancy.ages;
          if (!this.guestsAgesOld) {
            this.guestsAgesOld = [...occupancy.ages];
          }
          this.newDetailSearch(this.routeParams.productId, this.routeParams.dateFrom, this.routeParams.duration, (this.guestsAges || this.routeParams.ages || [35, 35]));
        });

        //  HolidayDetails Subscription
        this.holidayDetailsSubscription = this._holidayBookingService.holidayDetails$.subscribe(holidayDetails => {
          this.holidayDetails = holidayDetails;
        });

        //  Selected Solution Subscription
        this.selectedSolutionSubscription = this._holidayBookingService.selectedSolution$.subscribe(selectedSolution => {
          if(!selectedSolution){
            this.selectedSolution = selectedSolution
          }

          if ((!selectedSolution || !this.guestsAges) && !selectedSolution) {
            return;
          }
          this.selectedSolution = selectedSolution;
          this.setBookingUrl();
          const loadingItemId = 'livealpi';
          this._loadingService.addClassicLoad(loadingItemId);
          this._holidayBookingService.getSolutionAdditionalLiveInfo(this.selectedSolution)
            .catch(e => {
              console.error('La SelectedSolution non è disponibile');
              // this.noDetail = true;
            })
            .finally(() => this._loadingService.removeClassicLoad(loadingItemId));
        });
      });
    });

    if(this.isLanding){
      this.newApiCallDebouncer$
        .pipe(
          tap(() => {
            //  Loader a tutto schermo solo se non ci sono ancora holidayDetails (prima chiamata)
            if (!this.holidayDetails) {
              this._loadingService.addLoad(this.loadingItem);
            }
            this._holidayBookingService.setDetailLoadingStatus(false, false, true);
          }),
          takeUntil(this.unsubscribe$),
          debounceTime(500),
        )
        .subscribe(() => {
          this._holidayBookingService.setDetailLoadingStatus(true);
          const dateStr = dayjs(this.selectedDepartureDate).format('DD/MM/YYYY');
          this.newDetailSearch(this.routeParams.productId, dateStr);  //  Nuova chiamata di dettaglio
        });
    }

    this._bookingService.occupancy$
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe(occupancy => {
        this.selectedOccupancy = occupancy;
        this.newApiCallDebouncer$.next(true);
        if (!this.selectedOccupancy) {
          return;
        }
        this.updateOccupnacyString();
      });

    this._holidayBookingService.selectedDepartureDate$
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((departureDate: Date) => {
        this.selectedDepartureDate = departureDate;
        this.newApiCallDebouncer$.next(true);
      });

  }

  goToHomeRoute(): void {
    this._navigationService.getRedirectUrl('/').then(
      (resolved) => {
        if (resolved.isWidget) {
          // se sono in un widget, eseguo il redirect dall'esterno tramite postMessage
          // this._postMessageService.redirect(resolved.url);
          this._router.navigate([resolved.url]);
        } else {
          // ... altrimenti, effettuo direttamente il redirect tramite window.location.href
          window.location.href = resolved.url;
        }
      }
    );
  }

  //  Effettua una nuova chiamata di dettaglio
  newDetailSearch(productIdFromUrlParams: string = null, departureDateFromUrlParams: string = null, durationFromUrlParams: string = null, ages: number[] = null) {
    // applico il filtro notti sulle solutions ritornate dalla chiamata del dettaglio SOLO SE non è visualizzata la
    // griglia solutions/prezzi in modo che se presente, si possono filtrare le notti dalla griglia stesso
    const applyFilterByNights = (this.brand?.detailPageConfig.showPriceGrid) ? false : true;
    const params = this.createDetailParams(departureDateFromUrlParams, departureDateFromUrlParams);
    const loadingItem: LoadingItem = {id: 'holiday-detail', message: 'Stiamo preparando l\'offerta per te...'};
    this._loadingService.addLoad(loadingItem);
    this._holidayDetailService.getFormattedHolidayDetail(params, applyFilterByNights, this.isLanding).toPromise()
      .then(holidayDetailResult => {
        if (!holidayDetailResult.id && holidayDetailResult.solutions.length === 0) {
          this.noDetail = true;
          return null;
        }
        // La ricerca non ha fornito risultati (vado in noDetail se era la prima ricerca e mostro l'alert se era a seguito di un cambio di occupancy)
        if (!holidayDetailResult) {
          this.holidayDetails ? this.onNoSolutionsForGivenOccupancy() : this.noDetail = true;
          return null;
        }
        //  La ricerca ha fornito risultati
        this.guestsAgesOld = [...this.guestsAges];
        this.formattedGuestsString = this.getFormattedGuestsString(this.guestsAges);
        // verifico se sono presenti solutions nell'oggetto ritornato dalla chiamata del dettaglio in modo da mostrare la pagina di dettaglio completa altrimenti, passo alla rotta generica di dettaglio con solo alcune info di base recuperate attraverso l'id dell'accomodation


        if (holidayDetailResult.solutions?.length > 0 || this.showNoAvailabilityDetail) {
          const priceShownMustBeLive = holidayDetailResult?.pluginSource === 52 && holidayDetailResult.tourOperatorId === 1016058;

          if (priceShownMustBeLive) {
            holidayDetailResult.solutions?.forEach((solution: IHolidayDetailSolution) => {
              solution.priceShownMustBeLive = priceShownMustBeLive;
            });
          }else {
            holidayDetailResult.solutions.forEach((solution: IHolidayDetailSolution) => {
              solution.isShownPriceLive = holidayDetailResult.pluginSource !== 52
            })
          }


          this._holidayBookingService.setData('holidayDetails', holidayDetailResult);
           if (holidayDetailResult.searchSolution && !this.isLanding) {
            this._holidayBookingService.setData('selectedSolution', holidayDetailResult.searchSolution);
          }
          this.allSolutionsToShow = holidayDetailResult.solutions;
          this.setSeoProperties(this.holidayDetails);
          this.setDestinationName(this.holidayDetails?.place);
        } else {
          this.getAllAvailableDates(holidayDetailResult).then(() => {
            //Decommentare riga successiva per intercettare il caso in cui non vi è nessuna disponibilità
            //this.allAvailableDates = [];
            if(this.allAvailableDates?.length){
              this.showNoAvailabilityDetail = false;
              const dateStr = dayjs(this.allAvailableDates[0]).format('DD/MM/YYYY');
              this.selectedDepartureDate = this.allAvailableDates[0];
              this.newDetailSearch(productIdFromUrlParams, dateStr, dateStr);
            } else {
              this.showNoAvailabilityDetail = true;
              this.newDetailSearch(productIdFromUrlParams, departureDateFromUrlParams, departureDateFromUrlParams);
              this._loadingService.removeLoad(loadingItem);
            }
          })

          // se sono stati passati i parametri di data di partenza, durata ed età passeggeri, allora gli inserisco come queryParams nella rotta di redirect al dettaglio generico
          /* const queryParams: Params | null = {};
          if (departureDateFromUrlParams) {
            queryParams.dateFrom = departureDateFromUrlParams;
          }
          if (durationFromUrlParams) {
            queryParams.duration = durationFromUrlParams;
          }
          if (ages?.length) {
            queryParams.ages = ages.join(',');
          }
          this._router.navigate([`/vacanze/dettaglio/${productIdFromUrlParams}/${productIdFromUrlParams}`], {queryParams}); */
        }
      })
      .finally(() => this._loadingService.removeLoad(loadingItem));

  }

  //  Restituisce oggetto contenente i parametri per la chiamata di dettaglio
  createDetailParams(dateFrom = null, dateTo = null): IHolidayDetailParams {
    let fromDate = !dateFrom ? this.routeParams.dateFrom : dateFrom;
    let toDate = !dateTo ? this.routeParams.dateTo : dateTo;
    const sameDate = fromDate === toDate;
    // Se fromDate e toDate sono uguali integro la ricerca +/-3gg
    fromDate = dayjs(fromDate, 'DD-MM-YYYY').subtract(sameDate ? 3 : 0, 'd').unix();
    toDate = dayjs(toDate, 'DD-MM-YYYY').add(sameDate ? 3 : 0, 'd').unix();
    return {
      accomodationId: parseInt(this.routeParams.productId, 10),
      fromDate,
      pluginSourceId: parseInt(this.routeParams.pluginSource, 10),
      sourceId: this.routeParams.packetId,
      toDate,
      topId: parseInt(this.routeParams.topId, 10),
      onlyNetQuotes: this.onlyNetQuotes,
      duration: this.routeParams.duration || 0
    };
  }

  //  Mostra messaggio di errore, nel caso in cui l'utente cercasse soluzioni per una occupancy differente ma non esistessero opzioni valide
  onNoSolutionsForGivenOccupancy() {
    Swal.fire({
      text: `Nessuna soluzione disponibile presso questa struttura per ${this._formatAges.transform(this.guestsAges, 'adN_chN_ages')}`,
      icon: 'error',
      showCancelButton: false,
      confirmButtonText: 'Chiudi',
      allowOutsideClick: false,
      allowEscapeKey: false,
      confirmButtonColor: '#E55717'
    }).then(() => {
      //  Resetta occupancy precedente
      this._bookingService.setNewAges([...this.guestsAgesOld]);
    });
  }

  //  Imposta titolo immagine e descrizione della pagina
  setSeoProperties(holiday) {
    this._seoService.setTitle(holiday.title);
    this._seoService.setImage(holiday.images[0]);
    this._seoService.setDescription(
      holiday.accomodation?.texts?.map(d => d.description).join(',') ||
      holiday.tour?.texts?.map(d => d.description).join(',')
    );
  }

  //  Trova il nome della località di destinazione
  setDestinationName(placeInfo): void {
    this.destinationName = placeInfo?.name ? placeInfo.name : '';
    if (placeInfo?.parentId) {
      this._getDataFromMapping.transform(placeInfo?.parentId, 'destination')
        .toPromise()
        .then(result => {
          if (result?.Name?.length > 1) {
            this.destinationName += `, ${result?.Name}`;
          }
        });
    }
  }

  // passato un array di età (referite agli ospiti) restituisce una stringa nella forma "x adulti e y bambini"
  getFormattedGuestsString(agesArray): string {
    return this._formatAges.transform(agesArray, 'adN_chN');
  }

  //  Imposta l'url per il booking flow
  setBookingUrl() {
    const packetId = this.selectedSolution.source;
    const dateFrom = this.dateToString(this.selectedSolution.dateFrom);
    const dateTo = this.routeParams.dateFrom;
    const agesString = this.guestsAges.join(',');
    const duration = (this.selectedSolution.nights) ? this.selectedSolution.nights : (this.selectedSolution.days - 1);
    const title = this.routeParams.title?.replace(/\//g, '-') || '';
    this.bookingUrl = `/vacanze/booking/${this.routeParams.productId}/${this.routeParams.topId}/${packetId}/${this.routeParams.pluginSource}/struttura/${title}/dal/${dateFrom}/al/${dateTo}/durata/${duration}/eta/${agesString}`;
  }

  //  Passata una data la restituisce sotto forma di stringa
  dateToString(date: Date) {
    return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;
  }

  ngOnDestroy(): void {
    this.holidayDetailsSubscription.unsubscribe();
    this.selectedSolutionSubscription.unsubscribe();
    this.ocupancySubscription.unsubscribe();

    // reimposto il margin bottom del footer a 0
    if (document.querySelector('footer')) {
      document.querySelector('footer').style.marginBottom = '0';
    }
  }

  private updateOccupnacyString(): void {
    this.formattedGuestsString = this._formatAges.transform(this.selectedOccupancy?.ages, 'adN_chN');
  }

  private getAllAvailableDates(holidayDetailResult: IHolidayDetail): Promise<any> {
    this.allAvailableDates = [];
    let prefetchDateParams = {} as IPrefetchDateParams;
      prefetchDateParams.accommodations = [Number(holidayDetailResult.accomodation.id)];
      return this._holidayPrefetchService.prefetchDate(prefetchDateParams).toPromise()
      .then((dates: string[]) => {
        if (!dates?.length) {
          throw new Error('No available dates!');
        }
        if (!holidayDetailResult.accommodationParentId || String(holidayDetailResult.accommodationParentId) === '') {
          this.allAvailableDates = [...new Set(dates)]
            ?.map((stringDate: string) => {
              return dayjs(stringDate, 'YYYY-MM-DD').isValid() ?
                dayjs(stringDate, 'YYYY-MM-DD').toDate() :
                null;
            })
            ?.filter((validDate: Date) => validDate);
        } else {
          prefetchDateParams.accommodations = [Number(holidayDetailResult.accommodationParentId)];
          this._holidayPrefetchService.prefetchDate(prefetchDateParams).toPromise()
            .then((dates: string[]) => {
              if (!dates?.length) {
                throw new Error('No available dates!');
              }
              let matchDates = [];
              this.allAvailableDates.forEach(date => {
                dates.forEach((stringDate) => {
                  const date1 = new Date(stringDate);
                  date1.setHours(0, 0, 0, 0);
                  const date2 = new Date(date);
                  date2.setHours(0, 0, 0, 0);
                  if (date1.getTime() === date2.getTime()) {
                    matchDates.push(stringDate);
                  }
                })
              })
              this.allAvailableDates = [...new Set(matchDates)]
                ?.map((stringDate: string) => {
                  return dayjs(stringDate, 'YYYY-MM-DD').isValid() ?
                    dayjs(stringDate, 'YYYY-MM-DD').toDate() :
                    null;
                })
                ?.filter((validDate: Date) => validDate);
            })
        }
      }
    )}
}
